// schema
import { MinSlot, MaxSlot, ExcludedSlots, IWorkingHours, IDaySetting } from "@schema";

// libraries
import * as React from "react";
import { createFragmentContainer } from "react-relay";
import withStyles from "@material-ui/core/styles/withStyles";
import { IconButton, Tooltip } from "@material-ui/core";
import { DragIndicator, Close } from "@material-ui/icons";

// components
import { CalendarHours } from "@clockwise/web-commons/src/components/calendar";
import { RelativeView, DisplayData } from "@clockwise/web-commons/src/components/relative-view";

// util
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";
import { nextTimeSlot } from "@util/time-slot.util";

// constants
import { daysOfWeek } from "@constants/date.constant";
import { Slot, timeSlots } from "@constants/time-slots.constant";

// internal
import { IProps, IState, HoursRegion } from "./DraggableWorkingHours.d";
import { draggableWorkingHoursFragments } from "./DraggableWorkingHours.gql";
import { styles, hourText as hourTextStyle } from "./DraggableWorkingHours.styles";

///////////////
// Component //
///////////////

class DraggableWorkingHoursComponent extends React.Component<IProps, IState> {
  private initialPageY = 0;
  private currentRegion?: HoursRegion;
  private isSouth?: boolean;
  private containerRef: React.RefObject<HTMLDivElement> = React.createRef();

  constructor(props: IProps) {
    super(props);

    this.state = this.getStateFromWorkingHours(props.workingHours);
  }

  public componentDidMount() {
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mouseup", this.onMouseUp);
  }

  public componentWillUnmount() {
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mouseup", this.onMouseUp);
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (
      nextProps.updatedWorkingHours &&
      nextProps.updatedWorkingHours !== this.props.updatedWorkingHours
    ) {
      this.setState(this.getStateFromWorkingHours(nextProps.updatedWorkingHours));
    }
  }

  private getStateFromWorkingHours = (workingHours: IWorkingHours) => {
    const regionsByDay: DisplayData<HoursRegion>[][] = new Array(7).fill(null).map((_) => []);

    workingHours.daySettings.forEach((ds) => {
      const regions: HoursRegion[] = [];
      let currentMinSlot = ds.setting.minSlot;
      let lastExcludedSlot: ExcludedSlots | undefined = undefined;

      [...ds.setting.excludedSlots].sort().forEach((excludedSlot) => {
        if (
          !lastExcludedSlot ||
          timeSlots.all.indexOf(excludedSlot) - timeSlots.all.indexOf(lastExcludedSlot) > 1
        ) {
          regions.push({
            index: regions.length,
            active: false,
            day: ds.day,
            minSlot: timeSlots.all.indexOf(currentMinSlot),
            maxSlot: timeSlots.all.indexOf(excludedSlot) - 1,
          });
        }

        lastExcludedSlot = excludedSlot;
        currentMinSlot = nextTimeSlot(excludedSlot) as MinSlot;
      });

      regions.push({
        index: regions.length,
        active: false,
        day: ds.day,
        minSlot: timeSlots.all.indexOf(currentMinSlot),
        maxSlot: timeSlots.all.indexOf(ds.setting.maxSlot),
      });

      regionsByDay[daysOfWeek.indexOf(ds.day)] = regions.map((region) => ({
        type: "WorkingHoursRegion",
        payload: region,
      }));
    });

    const {
      minStart,
      secondMinStart,
      minStartCount,
      maxEnd,
      secondMaxEnd,
      maxEndCount,
    } = this.recalculateMinStartMaxEnd(regionsByDay);

    return {
      minStart,
      secondMinStart,
      minStartCount,
      maxEnd,
      secondMaxEnd,
      maxEndCount,
      minSlotOffset: Math.max(0, minStart - 2),
      maxSlotOffset: Math.min(47, maxEnd + 2),
      secondMinSlotOffset: Math.max(0, secondMinStart - 2),
      secondMaxSlotOffset: Math.min(47, secondMaxEnd + 2),
      currentCreateDayIndex: 0,
      regionsByDay,
    };
  };

  private recalculateMinStartMaxEnd = (regionsByDay: IState["regionsByDay"]) => {
    const totalRegions = regionsByDay.reduce((prev, curr) => prev + curr.length, 0);

    if (!totalRegions) {
      return {
        minStart: 18,
        secondMinStart: 18,
        maxEnd: 34,
        secondMaxEnd: 34,
        minStartCount: 1,
        maxEndCount: 1,
      };
    }

    let minStart = 47;
    let secondMinStart = 47;
    let maxEnd = 0;
    let secondMaxEnd = 0;

    let minStartCount = 1;
    let maxEndCount = 1;

    regionsByDay.forEach((regions) =>
      regions.forEach((region) => {
        if (minStart > region.payload.minSlot) {
          secondMinStart = Math.min(secondMinStart, minStart);
          minStart = region.payload.minSlot;
          minStartCount = 1;
        } else if (minStart === region.payload.minSlot) {
          minStartCount += 1;
        } else {
          secondMinStart = Math.min(secondMinStart, region.payload.minSlot);
        }

        if (maxEnd < region.payload.maxSlot) {
          secondMaxEnd = Math.max(secondMaxEnd, maxEnd);
          maxEnd = region.payload.maxSlot;
          maxEndCount = 1;
        } else if (maxEnd === region.payload.maxSlot) {
          maxEndCount += 1;
        } else {
          secondMaxEnd = Math.max(secondMaxEnd, region.payload.maxSlot);
        }
      }),
    );

    return { minStart, secondMinStart, maxEnd, secondMaxEnd, minStartCount, maxEndCount };
  };

  private maybeMergeRegionsForDay = (dayIndex: number) => {
    const regionsForDaySortedByMinSlot = this.state.regionsByDay[dayIndex].sort(
      (a, b) => a.payload.minSlot - b.payload.minSlot,
    );
    const newRegions = [];

    if (!regionsForDaySortedByMinSlot.length) {
      return;
    }

    let prevMinSlot = regionsForDaySortedByMinSlot[0].payload.minSlot;
    let prevMaxSlot = regionsForDaySortedByMinSlot[0].payload.maxSlot;

    regionsForDaySortedByMinSlot.slice(1).forEach((hoursRegionDisplayData) => {
      const currMinSlot = hoursRegionDisplayData.payload.minSlot;
      const currMaxSlot = hoursRegionDisplayData.payload.maxSlot;

      if (
        (currMinSlot >= prevMinSlot && currMinSlot <= prevMaxSlot) ||
        (currMaxSlot >= prevMinSlot && currMaxSlot <= prevMaxSlot) ||
        currMinSlot - 1 === prevMaxSlot
      ) {
        prevMaxSlot = Math.max(currMaxSlot, prevMaxSlot);
      } else {
        newRegions.push({
          type: "WorkingHoursRegion",
          payload: {
            active: false,
            day: daysOfWeek[dayIndex],
            index: newRegions.length,
            minSlot: prevMinSlot,
            maxSlot: prevMaxSlot,
          },
        });

        prevMinSlot = currMinSlot;
        prevMaxSlot = currMaxSlot;
      }
    });

    newRegions.push({
      type: "WorkingHoursRegion",
      payload: {
        active: false,
        day: daysOfWeek[dayIndex],
        index: newRegions.length,
        minSlot: prevMinSlot,
        maxSlot: prevMaxSlot,
      },
    });

    this.state.regionsByDay[dayIndex] = newRegions;
  };

  private onMouseDown = (hoursRegion: HoursRegion, isSouth: boolean) => (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    if (e.button !== 0) {
      return;
    }

    this.initialPageY = e.pageY;
    this.currentRegion = hoursRegion;
    this.isSouth = isSouth;

    this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)][
      hoursRegion.index
    ].payload.active = true;

    this.setState({
      regionsByDay: this.state.regionsByDay,
    });
  };

  private onMouseMove = (e: MouseEvent) => {
    const diff = this.initialPageY - e.pageY;

    if (this.isSouth === undefined && !diff) {
      return;
    } else if (this.isSouth === undefined) {
      this.isSouth = diff < 0;
    }

    const { currentRegion: hoursRegion, isSouth } = this;

    if (!hoursRegion) {
      return;
    }

    const containerHeight =
      (this.containerRef.current && this.containerRef.current.offsetHeight - 41) || 0;

    const spacePerSlot =
      containerHeight /
      (Math.min(48, this.state.maxSlotOffset + 1) - this.state.minSlotOffset || 1);
    const spacePerSecondMinSlot =
      containerHeight /
      (Math.min(48, this.state.maxSlotOffset + 1) - this.state.secondMinSlotOffset || 1);
    const spacePerSecondMaxSlot =
      containerHeight /
      (Math.min(48, this.state.secondMaxSlotOffset + 1) - this.state.minSlotOffset || 1);

    const isExpanding =
      (isSouth && diff < 0 && hoursRegion.maxSlot + 1 === this.state.maxEnd) ||
      (!isSouth && diff > 0 && hoursRegion.minSlot === this.state.minStart);

    const isContracting =
      (isSouth &&
        diff > 0 &&
        hoursRegion.maxSlot + 1 === this.state.maxEnd &&
        this.state.maxEndCount === 1) ||
      (!isSouth &&
        diff < 0 &&
        hoursRegion.minSlot === this.state.minStart &&
        this.state.minStartCount === 1);

    const maxSlotBuffer = this.state.maxSlotOffset - this.state.maxEnd;
    const secondMaxSlotBuffer = this.state.secondMaxSlotOffset - this.state.secondMaxEnd;
    const minSlotBuffer = this.state.minStart - this.state.minSlotOffset;
    const secondMinSlotBuffer = this.state.secondMinStart - this.state.secondMinSlotOffset;

    const availableContractingSpace =
      spacePerSecondMinSlot * secondMinSlotBuffer - spacePerSlot * minSlotBuffer;
    const availableContractingSpaceSouth =
      spacePerSecondMaxSlot * secondMaxSlotBuffer - spacePerSlot * maxSlotBuffer;

    const adjustedSpacePerSlot = ~~(!isExpanding && !isContracting
      ? spacePerSlot
      : isExpanding
      ? !isSouth
        ? (spacePerSlot * minSlotBuffer) / this.state.minStart
        : (spacePerSlot * maxSlotBuffer) / (47 - this.state.maxEnd + 1 || 1)
      : !isSouth
      ? availableContractingSpace / (this.state.secondMinStart - this.state.minStart)
      : availableContractingSpaceSouth / (this.state.maxEnd - this.state.secondMaxEnd));

    if (diff >= adjustedSpacePerSlot || diff <= -adjustedSpacePerSlot) {
      const slotAdjustment = diff < 0 ? -1 : 1;
      this.initialPageY -= slotAdjustment * adjustedSpacePerSlot;
      hoursRegion[isSouth ? "maxSlot" : "minSlot"] = Math.max(
        isSouth ? hoursRegion.minSlot : 0,
        Math.min(
          !isSouth ? hoursRegion.maxSlot : 47,
          hoursRegion[isSouth ? "maxSlot" : "minSlot"] - slotAdjustment,
        ),
      );

      const {
        minStart,
        secondMinStart,
        minStartCount,
        maxEnd,
        secondMaxEnd,
        maxEndCount,
      } = this.recalculateMinStartMaxEnd(this.state.regionsByDay);

      if (minStart !== this.state.minSlotOffset || maxEnd !== this.state.maxSlotOffset) {
        this.setState({
          minStart,
          secondMinStart,
          minStartCount,
          maxEnd,
          secondMaxEnd,
          maxEndCount,
          minSlotOffset: Math.max(0, minStart - 2),
          maxSlotOffset: Math.min(maxEnd + 2, 47),
          secondMinSlotOffset: Math.max(0, secondMinStart - 2),
          secondMaxSlotOffset: Math.min(secondMaxEnd + 2, 47),
          regionsByDay: this.state.regionsByDay,
        });
      } else {
        this.setState({
          regionsByDay: this.state.regionsByDay,
        });
      }
    }
  };

  private onMouseUp = () => {
    const { currentRegion: hoursRegion } = this;

    if (!hoursRegion) {
      return;
    }

    delete this.currentRegion;

    this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)][
      hoursRegion.index
    ].payload.active = false;

    if (
      this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)][hoursRegion.index].payload
        .minSlot >
      this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)][hoursRegion.index].payload
        .maxSlot
    ) {
      this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)].splice(hoursRegion.index, 1);
      this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)]
        .slice(hoursRegion.index)
        .forEach((hr) => hr.payload.index--);
    }

    this.maybeMergeRegionsForDay(daysOfWeek.indexOf(hoursRegion.day));

    this.setState(
      {
        regionsByDay: this.state.regionsByDay,
      },
      this.onChange,
    );
  };

  private onMouseDownCreate = (dayIndex: number) => (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      !this.containerRef.current ||
      e.button !== 0 ||
      (!this.props.enableSplitHours && this.state.regionsByDay[dayIndex].length)
    ) {
      return;
    }

    const containerHeight = this.containerRef.current.offsetHeight - 40;

    const elementPageY =
      e.currentTarget.getBoundingClientRect().top - document.body.getBoundingClientRect().top;

    const adjustedClientY = e.pageY - elementPageY;

    const initialSlot = Math.round(
      adjustedClientY /
        (containerHeight / (this.state.maxSlotOffset + 1 - this.state.minSlotOffset)),
    );

    const hoursRegion = {
      day: daysOfWeek[dayIndex],
      active: true,
      index: this.state.regionsByDay[dayIndex].length,
      minSlot: initialSlot + this.state.minSlotOffset,
      maxSlot: initialSlot + this.state.minSlotOffset,
    };

    this.initialPageY = e.pageY;
    this.currentRegion = hoursRegion;
    this.isSouth = undefined;

    this.state.regionsByDay[dayIndex].push({
      type: "WorkingHoursRegion",
      payload: hoursRegion,
    });

    this.setState({
      regionsByDay: this.state.regionsByDay,
    });
  };

  private onSplice = (hoursRegion: HoursRegion, slot: number) => () => {
    this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)].splice(
      hoursRegion.index,
      1,
      {
        type: "WorkingHoursRegion",
        payload: {
          ...hoursRegion,
          maxSlot: slot,
        },
      },
      {
        type: "WorkingHoursRegion",
        payload: {
          ...hoursRegion,
          index: hoursRegion.index + 1,
          minSlot: slot + 2,
        },
      },
    );

    this.state.regionsByDay[daysOfWeek.indexOf(hoursRegion.day)]
      .slice(hoursRegion.index + 2)
      .forEach((hr) => hr.payload.index++);

    this.setState(
      {
        regionsByDay: this.state.regionsByDay,
      },
      this.onChange,
    );
  };

  private onChange = () => {
    if (this.props.onChange) {
      this.props.onChange(this.stateToWorkingHours());
    }
  };

  private onClickRemoveRegion = (dayIndex: number, hoursRegion: HoursRegion) => () => {
    const updatedRegionsByDay = [...this.state.regionsByDay];
    updatedRegionsByDay[dayIndex].splice(hoursRegion.index, 1);
    updatedRegionsByDay[dayIndex].forEach((region, index) => (region.payload.index = index));

    this.setState(
      {
        regionsByDay: updatedRegionsByDay,
      },
      this.onChange,
    );
  };

  private stateToWorkingHours = (): IWorkingHours => {
    const { workingHours } = this.props;

    const daySettings: IDaySetting[] = [];

    this.state.regionsByDay.forEach((hoursRegions, i) => {
      let minSlot: MinSlot = MinSlot.T_23_30;
      let maxSlot: MaxSlot = MaxSlot.T_00_00;
      const excludedSlots: ExcludedSlots[] = [];
      let lastMaxSlot: number | undefined = undefined;

      hoursRegions.forEach((hoursRegion) => {
        minSlot =
          hoursRegion.payload.minSlot < timeSlots.all.indexOf(minSlot)
            ? (timeSlots.all[hoursRegion.payload.minSlot] as MinSlot)
            : minSlot;

        maxSlot =
          hoursRegion.payload.maxSlot > timeSlots.all.indexOf(maxSlot)
            ? (timeSlots.all[hoursRegion.payload.maxSlot] as MaxSlot)
            : maxSlot;

        if (lastMaxSlot !== undefined) {
          if (hoursRegion.payload.minSlot > lastMaxSlot) {
            for (let y = lastMaxSlot + 1; y < hoursRegion.payload.minSlot; y++) {
              excludedSlots.push(timeSlots.all[y] as ExcludedSlots);
            }
          }
        }

        lastMaxSlot = hoursRegion.payload.maxSlot;
      });

      if (hoursRegions.length) {
        daySettings.push({
          __typename: "DaySetting",
          day: daysOfWeek[i],
          setting: {
            minSlot,
            maxSlot,
            excludedSlots,
            __typename: "WorkingSetting",
          },
        });
      }
    });

    return {
      ...workingHours,
      daySettings,
    };
  };

  private renderHoursRegion = (hoursRegion: HoursRegion) => {
    const { classes } = this.props;

    const { minSlotOffset: minStart, maxSlotOffset: maxEnd } = this.state;

    const minSlot = Math.min(47, Math.max(0, hoursRegion.minSlot));
    const maxSlot = Math.min(47, Math.max(0, hoursRegion.maxSlot));

    const forceTooltipOpenProps = hoursRegion.active ? { open: true } : {};

    return (
      <React.Fragment>
        <div
          className={`${classes.workingHoursContainer}${hoursRegion.active ? " active" : ""}`}
          style={{
            top: `${Math.min(((minSlot - minStart) / (maxEnd + 1 - minStart)) * 100, 100)}%`,
            bottom: `${Math.max(
              100 - ((maxSlot + 1 - minStart) / (maxEnd + 1 - minStart)) * 100,
              0,
            )}%`,
          }}
        >
          <div className="remove-shell" />
          {!hoursRegion.active ? (
            <Tooltip
              title={`I don't work between ${timeSlots.min[timeSlots.all[minSlot] as Slot]} - ${
                timeSlots.max[timeSlots.all[maxSlot] as Slot]
              } on ${hoursRegion.day}`}
            >
              <IconButton
                className="remove"
                onClick={this.onClickRemoveRegion(daysOfWeek.indexOf(hoursRegion.day), hoursRegion)}
              >
                <Close />
              </IconButton>
            </Tooltip>
          ) : null}
          <div className="rounded-container">
            <Tooltip
              key={hoursRegion.active ? "drag-tooltip-1-active" : "drag-tooltip-1"}
              title={timeSlots.min[timeSlots.all[minSlot] as Slot]}
              {...forceTooltipOpenProps}
            >
              <div className="drag-handle north" onMouseDown={this.onMouseDown(hoursRegion, false)}>
                <DragIndicator className="drag-indicator" />
              </div>
            </Tooltip>
            <div className={`body${hoursRegion.active ? " active" : ""}`} />
            <Tooltip
              key={hoursRegion.active ? "drag-tooltip-2-active" : "drag-tooltip-2"}
              title={timeSlots.max[timeSlots.all[maxSlot] as Slot]}
              {...forceTooltipOpenProps}
            >
              <div className="drag-handle south" onMouseDown={this.onMouseDown(hoursRegion, true)}>
                <DragIndicator className="drag-indicator" />
              </div>
            </Tooltip>
          </div>
        </div>
        {!hoursRegion.active && this.props.enableSplitHours
          ? [...new Array(Math.max(maxSlot - minSlot - 1, 0)).keys()].map((_, i) => (
              <div
                key={i}
                className={classes.slot}
                style={{
                  top: `${Math.min(
                    ((minSlot + i + 1 - minStart) / (maxEnd + 1 - minStart)) * 100,
                    100,
                  )}%`,
                  bottom: `${Math.max(
                    100 - ((minSlot + i + 1 - minStart) / (maxEnd + 1 - minStart)) * 100,
                    0,
                  )}%`,
                }}
                onClick={this.onSplice(hoursRegion, minSlot + i)}
              />
            ))
          : null}
      </React.Fragment>
    );
  };

  public render() {
    const { classes } = this.props;
    const { minSlotOffset, maxSlotOffset } = this.state;

    const slotDiff = Math.min(48, maxSlotOffset + 1) - minSlotOffset;

    const displayDataByDay = this.state.regionsByDay;

    const dataMappers = {
      WorkingHoursRegion: this.renderHoursRegion,
    };

    return (
      <div className={classes.container}>
        <div className={classes.horizontalGridContainer}>
          <div className="relative-view">
            {new Array(slotDiff).fill(null).map((_n, i) => {
              const globalSlotIndex = i - minSlotOffset;

              if (i !== 0 && globalSlotIndex % 2 !== 0) {
                return null;
              }

              const top = i === 0 ? -1 : `${(100 * i) / slotDiff}%`;

              return <div key={i} className="horizontal-grid" style={{ top }} />;
            })}
          </div>
        </div>
        <div className="hours">
          <CalendarHours
            hourTextStyle={hourTextStyle}
            minSlotOffset={minSlotOffset}
            maxSlotOffset={Math.min(48, maxSlotOffset + 1)}
            showEveryN={2}
            showBorder={false}
          />
        </div>
        {displayDataByDay.map((d, i) => (
          <div
            key={i}
            className={`relative-view-wrapper${d.length ? "" : " empty"}`}
            ref={this.containerRef}
          >
            <div className="day-name">
              {new ZonedMoment().startOf("week").add(i, "days").format("ddd").toUpperCase()}
            </div>
            <RelativeView className="relative-view" displayData={d} dataMappers={dataMappers}>
              <div
                onMouseDown={this.onMouseDownCreate(i)}
                style={{ position: "absolute", width: "100%", height: "100%" }}
              />
            </RelativeView>
          </div>
        ))}
        {this.props.children}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const DraggableWorkingHoursStyled = withStyles(styles)(DraggableWorkingHoursComponent);

///////////
// Relay //
///////////

export const DraggableWorkingHours = createFragmentContainer(
  DraggableWorkingHoursStyled,
  draggableWorkingHoursFragments,
);
